import { Injectable, TemplateRef, signal } from '@angular/core'
import { MatDrawerMode } from '@angular/material/sidenav'

/**
 * Das Side Sheet der App — **eine** Fassung fuer alle drei Apps.
 *
 * Es faehrt rechts unter der Toolbar ein, als abgerundete Flaeche neben der Inhaltsflaeche, und traegt
 * Titel (optional Untertitel und Zurueck-Pfeil), Schliessen-Knopf und einen frei projizierten Inhalt
 * mit festem Rand. Eine Aktionsleiste am Fuss ist die globale Klasse `.sidesheet-fussleiste`. Den Rahmen rendert `lib-navigation` genau einmal; Seiten rufen nur
 * `open(...)`.
 *
 * **Es gibt bewusst keine zweite Sorte.** Bis 2026-08-24 stand derselbe Drawer-Block dreimal
 * wortgleich in den drei App-Navigationen, der Service zweimal identisch daneben — und der
 * Abrechnungs-Tab hatte sich obendrein eine eigene, anders aussehende Fassung gebaut (Ansage Tim:
 * „ich will das schon irgendwie harmonisiert haben, anwendungsweit, als wenn wir eine
 * Standardkomponente haben"). Wem hier etwas fehlt, erweitert diese Komponente — er baut keine
 * zweite daneben.
 */
export interface SidesheetConfig {
  title: string
  templateRef: TemplateRef<unknown>
  mode?: MatDrawerMode
  size?: string
  /**
   * Knoepfe in der Kopfzeile, links vom Schliessen-Knopf (z.B. Verlauf und „Neues Gespraech" des
   * Assistenten). Was zum Sheet als Ganzem gehoert, steht oben; was zum Inhalt gehoert, im Inhalt.
   */
  actions?: TemplateRef<unknown>
  /** Wird bei jedem Klick auf den Titel gerufen. Der Titel sieht dabei unveraendert aus (Easter Egg im Hilfe-Sheet). */
  titleClick?: () => void
  /** Zweite Zeile unter dem Titel, etwa „Schritt 1 von 2 · Vertrag finden". Aendert sich mit `aendern(...)`. */
  untertitel?: string
  /**
   * Zeigt links vom Titel einen Zurueck-Pfeil, der diese Funktion ruft — fuer Schritte INNERHALB eines
   * Sheets. Ein eigener „Zurueck"-Knopf im Inhalt entfaellt damit.
   */
  zurueck?: () => void
  /**
   * Der Inhalt bekommt keinen Rand und fuellt die Flaeche selbst (Support-Chat mit fester Eingabe,
   * Aufgabe mit fester Statusleiste). Alle anderen bekommen den Rand vom Rahmen und bringen keinen mit.
   */
  randlos?: boolean
}

@Injectable({
  providedIn: 'root',
})
export class SidesheetService {
  readonly sidesheet = signal<SidesheetConfig | undefined>(undefined)

  open(title: string, templateRef: TemplateRef<unknown>, config?: Omit<SidesheetConfig, 'title' | 'templateRef'>) {
    this.sidesheet.set({
      title,
      templateRef,
      mode: config?.mode,
      size: config?.size,
      actions: config?.actions,
      titleClick: config?.titleClick,
      untertitel: config?.untertitel,
      zurueck: config?.zurueck,
      randlos: config?.randlos,
    })
  }

  /**
   * Aendert den Kopf des offenen Sheets: Untertitel und Zurueck-Pfeil, wenn der Inhalt einen Schritt
   * weitergeht, den Titel, wenn er erst mit dem Inhalt feststeht (die Aufgabe nennt dort ihren eigenen).
   */
  aendern(kopf: Partial<Pick<SidesheetConfig, 'title' | 'untertitel' | 'zurueck'>>) {
    this.sidesheet.update((sheet) => (sheet ? { ...sheet, ...kopf } : sheet))
  }

  close() {
    this.sidesheet.set(undefined)
  }
}
